// 優良臨床教師投票專區整頁（vote-excellent-teacher＝legacy vote/excellentTeacher.jsp）。
// 批次 21（Gitea issue #24，B8-3 未涵蓋）。掛在 PageBehavior.complexBody（DisplayPage 早退，
// 批次 17 display 頁先例）。
//
// legacy 流程（行號親驗 2026-09-27；基準＝views/vote/excellentTeacher.jsp(171，prompt 版型無
// breadcrumb)＋js-my/vote/excellentTeacher.js(228)＋excellentTeacherUtils.js.gz(40)；詳
// docs/plans/2026-09-27-batch21-vote-pages-plan.md §1.2）：
//   - 入口（VoteController.java:113-120）：ExcellentTeacherDataCriteria==null→'redirect:/main'
//     （raw-dom vote-excellent-teacher.html＝**教師首頁捕獲**＝鐵證）；criteria 只發給學生身分＋
//     活動中投票＋未投過票者（setModelAndViewIfNeedVote :132-180＝入口流程，D21-1 不鏡像，以頁內
//     role gate 等價：role≠student 或名單空→站內導航 /main）。
//   - ready（js:13-18）＋setVoteInfo(:20-27)：loginAccountJobTypeId==1→UGY_INFO else OTHER_INFO
//     （:9-10 逐字，OTHER_INFO 含 <br>；D21-3 以種子預設 UGY）。
//   - 卡片（jsp:115-129 teacherCardTmpl）：col-md-3 teacherCard voted=false；img getPhoto?userId=
//     {{id}}（placeholder）；科部:{{departmentName}}／姓名:{{name}}；debugMode→來源:{{expansion1}}
//     （jsp:121-123；D21-9 以 URL query 鏡像）。
//   - 名單（js:99-122/:124-146）：list>0→maxVote=Math.ceil(list.length/2)（:104/:129）＋row attr
//     maxVote＋span.maxVote 文字＋cards；else row hide。getTeacherList（utils.gz:10-40 → GET
//     vote/getExcellentTeacherData；VoteController.java:163-198 criteria null→resp 查詢失敗＝
//     名單空→showResponse 同路徑）。
//   - 投票（js:49-72）：voteCnt<maxVote→voted＋.voted class（黃框）＋refreshVoteCnt(:74-79)；
//     else showAlertDialog('已達['+type+']投票上限！')(:62)（type＝teacherType h3 文字）。
//   - 無資料（js:84-97）：showResponse('查無投票資料','請聯絡教學部人員')(:88)。
//   - showResponse(:175-194)：$.confirm（isSuccess→blue else red；closeIcon false；icon
//     fa-info-circle）**單鈕『回首頁』**→ window.location.href='main'——雛形以 alert（單 OK 鈕）
//     鏡像後站內導航 /main（DialogsApi.confirm 是雙鈕語意不合；D21-11 備註）。
//   - 送出（js:196-224）：getVoteResult(:148-173)＝**residents 先**、attending 後（{teacherId,
//     voteResult,teacherCategory}）→sysConfirm('確定要送出投票嗎?')(:198)→POST
//     vote/excellentTeacherSubmit {voteList}(:199-217)→success showResponse('投票成功',desc,true)／
//     fail showResponse('系統錯誤',desc)；**ajax error handler 引用未定義 resp＝ReferenceError
//     （legacy bug）→無對話框**（D21-6 照實：雛形 catch 後不開對話框）。
//   - server（VoteController.java:129-155）：非投票者不可投票(:152)／投票失敗，請聯絡系統管理員！(:148)
//     ／投票成功(:134)（D21-2：React 以種子 voted 旗標等價，身分驗證屬真後端）。
//
// 【證據等級】§7 第 3 級（原始碼）。種子【雛形值待實機校準】。

import { useEffect, useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/base/Button';
import { ProcessingOverlay, useDialogs } from '@/components/layouts';
import { useDataSource, useSession } from '@/mocks';
import { kebab } from '../main/dashboard/dashboardParts';
import type { Row } from '@/mocks/types';

/** 投票說明常數（js:9-10 逐字；UGY 版種子預設＝D21-3；OTHER_INFO 含 <br>、由 loginAccountJobTypeId
 *  開關（Session 無 jobType 欄位＝不鏡像）；UGY 版＝js:9，OTHER 版＝js:10（另見 docs §1.2）。 */
const UGY_INFO =
  '投票說明：每個人最多可投票數為可投老師總數的一半（無條件進位），且一位學生對一位老師只能計入1票。例如: 學生投票名單有 99 位住院醫師及 50 位主治醫師，該生最多可投給 50 位(99╳50％) 住院醫師各一票及 25 位(50╳50％) 主治醫師各一票。'; // js:9

/** 訊息字面值（excellentTeacher.js；！＝FF01、?＝003F）。 */
const TEXT_VOTE_LIMIT = (type: string) => `已達[${type}]投票上限！`; // js:62
const TEXT_NO_DATA_TITLE = '查無投票資料'; // js:88
const TEXT_NO_DATA_CONTENT = '請聯絡教學部人員'; // js:88
const TEXT_SUBMIT_CONFIRM = '確定要送出投票嗎?'; // js:198
const TEXT_SUBMIT_SUCCESS_TITLE = '投票成功'; // js:208（title）
const TEXT_SUBMIT_SUCCESS_DESC = '投票成功'; // resp.desc＝投票成功（VoteController.java:134；F1 修）
// 回首頁（js:181-185）：showResponse 的單鈕文字；雛形以 alert（單 OK 鈕）鏡像＝鈕文字不另呈現。

/** 投票卡片（jsp:115-129 teacherCardTmpl：voted attr＋黃框 .voted）。 */
function TeacherCard(props: {
  readonly pageId: string;
  readonly teacher: Row;
  readonly voted: boolean;
  readonly debugMode: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const id = String(props.teacher['id'] ?? '');
  return (
    <div
      data-ui-id={`${props.pageId}-card-${kebab(id)}`}
      data-voted={props.voted ? 'true' : 'false'}
      className="m-[2px] p-1"
    >
      <div
        data-ui-id={`${props.pageId}-card-${kebab(id)}-body`}
        className={`m-auto h-56 w-64 rounded-xl border-4 ${props.voted ? 'border-[#dbf205]' : 'border-[#bbc6c7]'} bg-white p-1 hover:cursor-pointer hover:bg-[#dff2424d]`}
        onClick={props.onToggle}
      >
        <div data-ui-id={`${props.pageId}-card-${kebab(id)}-img-row`} className="m-auto">
          <span
            data-ui-id={`${props.pageId}-card-${kebab(id)}-img`}
            title={`getPhoto?userId=${id}【雛形值待實機校準：照片素材無法自 legacy 抽出】`}
            className="mx-auto mt-[5%] block h-[120px] w-[120px] rounded bg-[#e8e8e8] text-center text-[36px] leading-[120px]"
          >
            ▣
          </span>
        </div>
        <div data-ui-id={`${props.pageId}-card-${kebab(id)}-data`} className="mx-[5%] mt-[5%]">
          <p data-ui-id={`${props.pageId}-card-${kebab(id)}-dept`} className="text-[18px] font-bold">
            科部:{String(props.teacher['departmentName'] ?? '')}
          </p>
          <p data-ui-id={`${props.pageId}-card-${kebab(id)}-name`} className="text-[18px] font-bold">
            姓名:{String(props.teacher['name'] ?? '')}
            {props.debugMode ? (
              <span data-ui-id={`${props.pageId}-card-${kebab(id)}-source`} style={{ fontSize: 12 }}>
                來源:{String(props.teacher['expansion1'] ?? '')}
              </span>
            ) : null}
          </p>
        </div>
      </div>
    </div>
  );
}

/** 名單區（jsp:146-155/:156-165：teacherType h3＋最多可推薦…已推薦…＋cards；空＝hide js:121/:143）。 */
function TeachersRow(props: {
  readonly pageId: string;
  readonly sectionKey: 'attending' | 'residents';
  readonly type: string;
  readonly rows: readonly Row[];
  readonly votedIds: Readonly<Record<string, boolean>>;
  readonly debugMode: boolean;
  readonly onToggle: (teacherId: string, type: string) => void;
}): ReactElement | null {
  const { pageId, sectionKey, type, rows, votedIds } = props;
  if (rows.length === 0) {
    return null; // list 空時 row hide（js:121-122/:143-145）
  }
  const maxVote = Math.ceil(rows.length / 2); // js:104/:129
  const voteCnt = rows.filter((teacher) => votedIds[String(teacher['id'])] === true).length;
  return (
    <div data-ui-id={`${pageId}-${sectionKey}-row`} data-max-vote={maxVote} className="mb-4">
      <h3 data-ui-id={`${pageId}-${sectionKey}-type`} className="bg-[#e1ded5c4] p-2 text-center font-bold text-black">
        {type}
      </h3>
      <div data-ui-id={`${pageId}-${sectionKey}-detail`} className="text-[18px] font-bold">
        最多可推薦{type} <span data-ui-id={`${pageId}-${sectionKey}-max-vote`}>{maxVote}</span> 員，已推薦{' '}
        <span data-ui-id={`${pageId}-${sectionKey}-vote-cnt`}>{voteCnt}</span> 員
      </div>
      <div data-ui-id={`${pageId}-${sectionKey}-cards`} className="flex flex-wrap">
        {rows.map((teacher) => (
          <TeacherCard
            key={String(teacher['id'])}
            pageId={pageId}
            teacher={teacher}
            voted={votedIds[String(teacher['id'])] === true}
            debugMode={props.debugMode}
            onToggle={() => props.onToggle(String(teacher['id']), type)}
          />
        ))}
      </div>
    </div>
  );
}

export function ExcellentTeacherVoteBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession();
  const [rows, setRows] = useState<readonly Row[] | null>(null);
  const [loading, setLoading] = useState(true);
  /** 勾選狀態（js:49-72 voted attr；送出成功→全數 voted 持久化＝criteria 清除→再訪 redirect 鏡像）。 */
  const [votedIds, setVotedIds] = useState<Readonly<Record<string, boolean>>>({});
  /** debugMode＝URL query（js:7；D21-9；以 router location 讀取＝MemoryRouter 測試等價，實機同 window.location）。 */
  const [debugMode] = useState(
    () => new URLSearchParams(location.search).get('debugMode') === 'true',
  );
  /** 一次性對話框閘（alert 只開一次；防 render 重入重複）。 */
  const [responseShown, setResponseShown] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const result = await dataSource.list(pageId, {});
        setRows(result.rows);
      } catch {
        // VoteController.java:195 criteria null→查詢失敗；雛形以空名單同路徑（showResponse）。
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // 單次載入。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** showResponse（js:175-194）：$.confirm 單鈕『回首頁』→鏡像為 alert（單 OK）＋站內導航 /main。 */
  const showResponse = async (title: string, content: string): Promise<void> => {
    await dialogs.alert(`${title}\n${content}`);
    navigate('/main'); // window.location.href='main'（js:183）
  };

  const data = rows ?? [];
  const attending = data.filter((row) => String(row['teacherCategory']) === 'ATTENDING_PHYSICIAN');
  const residents = data.filter((row) => String(row['teacherCategory']) === 'RESIDENT');
  // 兩種「不可再投」狀態分流（F2 修）：
  //   voted 全 1＝criteria 已清除鏡像（VoteController.java:146 session.removeAttribute）→
  //   再訪＝criteria==null→redirect:/main（:119）＝**無對話框直接導航**。
  //   名單本身空（種子無資料）＝criteria!=null 但名單空→showResponse 查無投票資料（js:88）。
  //   判定限教師列（teacherCategory 有值）——generic fixture 列無此欄不參與判定。
  const teacherRows = data.filter((row) => row['teacherCategory'] !== undefined);
  const criteriaActive = teacherRows.some((row) => String(row['voted'] ?? '0') === '0');
  const hasAny = attending.length > 0 || residents.length > 0;
  const allVoted = hasAny && !criteriaActive;

  // 無名單分流（F2 修）：已投完（allVoted）＝criteria==null redirect 直接導航無對話框（:119）；
  // 名單空＝showResponse 查無投票資料（js:84-97；一次）。
  useEffect(() => {
    if (loading) return;
    if (allVoted) {
      navigate('/main');
      return;
    }
    if (hasAny) return;
    if (!responseShown) {
      setResponseShown(true);
      void showResponse(TEXT_NO_DATA_TITLE, TEXT_NO_DATA_CONTENT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasAny, allVoted, responseShown]);

  // role gate（VoteController.java:113-120 criteria==null→redirect:/main 鏡像；D21-1）。
  useEffect(() => {
    if (!loading && session.role !== 'student') {
      navigate('/main');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, session.role]);

  /** 投票（js:49-72）：voteCnt<maxVote→voted；else alert 已達[type]投票上限！(:62)。 */
  const voteTeacher = (teacherId: string, type: string): void => {
    const section = type === '主治醫師' ? attending : residents;
    const maxVote = Math.ceil(section.length / 2);
    const voteCnt = section.filter((row) => votedIds[String(row['id'])] === true).length;
    if (votedIds[teacherId] !== true) {
      if (voteCnt < maxVote) {
        setVotedIds((current) => ({ ...current, [teacherId]: true }));
      } else {
        void dialogs.alert(TEXT_VOTE_LIMIT(type));
        return;
      }
    } else {
      setVotedIds((current) => ({ ...current, [teacherId]: false }));
    }
  };

  /**
   * 送出（js:196-224）：confirm→逐列 update(voted)（全數持久化＝criteria 清除鏡像，D21-2；
   * POST /vote/excellentTeacherSubmit 未映射＝C5-j 誠實標註）→showResponse(投票成功)；
   * catch＝legacy error handler ReferenceError 照實（D21-6：不開對話框）。
   * voteList 順序＝residents 先、attending 後（js:148-173）。
   */
  const submit = async (): Promise<void> => {
    const confirmed = await dialogs.confirm(TEXT_SUBMIT_CONFIRM);
    if (!confirmed) return;
    const voteList = [...residents, ...attending].map((row) => ({
      teacherId: String(row['id']),
      voteResult: votedIds[String(row['id'])] === true ? 1 : 0,
      teacherCategory: String(row['teacherCategory']),
    }));
    try {
      // criteria 清除哨兵（複驗 F2 修）：legacy 送出成功＝session.removeAttribute(criteria)
      // （VoteController.java:146）→再訪＝criteria==null redirect（:119）。逐列 voteResult 僅在
      // 送出 payload 鏡像（上方 voteList），mock 不存＝D21-2；persist 全列 voted=1＝清除標記。
      for (const vote of voteList) {
        await dataSource.update(pageId, vote.teacherId, { voted: 1 });
      }
      setResponseShown(true);
      // showResponse('投票成功', resp.desc, true)（js:208）；resp.desc＝投票成功（VoteController.java:134 鏡像）。
      await showResponse(TEXT_SUBMIT_SUCCESS_TITLE, TEXT_SUBMIT_SUCCESS_DESC);
    } catch {
      // legacy ajax error handler 引用未定義 resp＝ReferenceError→無對話框（D21-6 照實）。
    }
  };

  if (loading || !hasAny || allVoted) {
    // 載入中／名單空（showResponse 已開，OK→/main）／已投完（redirect /main 中）。
    return <ProcessingOverlay uiId={`${pageId}-loading`} open />;
  }

  return (
    <div data-ui-id={pageId}>
      <div data-ui-id={`${pageId}-container`} className="mx-auto max-w-4xl bg-white p-4">
        <h1 data-ui-id={`${pageId}-title`} className="text-center text-2xl font-bold">
          優良臨床教師投票專區
        </h1>
        <hr className="my-2" />
        {/* 投票說明（jsp:143-145 p.voteInfo＋js:20-27 setVoteInfo：UGY/OTHER 開關；D21-3 預設 UGY，
            OTHER_INFO 不鏡像＝loginAccountJobTypeId 無 Session 欄位）。 */}
        <p data-ui-id={`${pageId}-vote-info`} className="text-[18px] font-bold text-[#7286e5]">
          {UGY_INFO}
        </p>
        <TeachersRow
          pageId={pageId}
          sectionKey="attending"
          type="主治醫師"
          rows={attending}
          votedIds={votedIds}
          debugMode={debugMode}
          onToggle={voteTeacher}
        />
        <TeachersRow
          pageId={pageId}
          sectionKey="residents"
          type="住院醫師"
          rows={residents}
          votedIds={votedIds}
          debugMode={debugMode}
          onToggle={voteTeacher}
        />
        <hr />
        <div data-ui-id={`${pageId}-footer`} className="my-5 flex items-center justify-center">
          <Button
            uiId={`${pageId}-submit`}
            variant="primary"
            type="button"
            dataEffect="update:vote-excellent-teacher"
            onClick={() => void submit()}
          >
            送出
          </Button>
        </div>
      </div>
    </div>
  );
}
